import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { BrandButton } from "../../../components/brand/BrandButton";
import { SignaturePreview } from "./SignaturePreview";
import { Loader2, CheckCircle2, XCircle, Shield } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/i18n";

export type AuthStep =
  | "idle"
  | "loading"
  | "preview"
  | "signing"
  | "success"
  | "error";

export interface SignatureDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  step: AuthStep;
  challengeMessage: string;
  walletAddress: string;
  error: string | null;
  isLoading: boolean;
  onConfirm: () => void;
  onRetry: () => void;
}

/**
 * SignatureDialog - Simple dialog for signature preview and confirmation
 *
 * Shows:
 * - Loading state while getting challenge
 * - Signature preview with confirm button
 * - Signing state
 * - Success/Error states
 */
export function SignatureDialog({
  open,
  onOpenChange,
  step,
  challengeMessage,
  walletAddress,
  error,
  isLoading,
  onConfirm,
  onRetry,
}: SignatureDialogProps) {
  const { t } = useTranslation();

  const renderContent = () => {
    switch (step) {
      case "loading":
        return (
          <div className="flex flex-col items-center justify-center py-8 space-y-4">
            <Loader2 className="w-12 h-12 text-brand-primary animate-spin" />
            <p className="text-sm text-text-muted">
              {t(
                "loginModal.preparingSignature",
                "Preparing signature request...",
              )}
            </p>
          </div>
        );

      case "preview":
        return (
          <div className="py-4 max-h-[60vh] overflow-y-auto">
            <SignaturePreview
              message={challengeMessage}
              walletAddress={walletAddress}
            />
          </div>
        );

      case "signing":
        return (
          <div className="flex flex-col items-center justify-center py-8 space-y-4">
            <div className="relative">
              <Loader2 className="w-12 h-12 text-brand-primary animate-spin" />
              <div className="absolute inset-0 w-12 h-12 rounded-full bg-brand-primary/15 blur-xl animate-pulse" />
            </div>
            <div className="text-center space-y-2">
              <p className="text-base font-medium text-text-primary">
                {t("loginModal.waitingForSignature")}
              </p>
              <p className="text-sm text-text-muted">
                {t("loginModal.approveInWallet")}
              </p>
            </div>
          </div>
        );

      case "success":
        return (
          <div className="flex flex-col items-center justify-center py-8 space-y-4">
            <div className="w-16 h-16 rounded-full bg-success/10 border border-success/30 flex items-center justify-center">
              <CheckCircle2 className="w-10 h-10 text-success" />
            </div>
            <p className="text-base font-medium text-success">
              {t("loginModal.authenticationSuccessful")}
            </p>
          </div>
        );

      case "error":
        return (
          <div className="flex flex-col items-center justify-center py-8 space-y-4">
            <div className="w-16 h-16 rounded-full bg-danger/10 border border-danger/30 flex items-center justify-center">
              <XCircle className="w-10 h-10 text-danger" />
            </div>
            <div className="text-center space-y-2">
              <p className="text-base font-medium text-danger">
                {t("loginModal.authenticationFailed")}
              </p>
              <p className="text-sm text-text-muted">
                {error || t("loginModal.errorOccurred")}
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const renderFooter = () => {
    switch (step) {
      case "loading":
        return (
          <DialogFooter>
            <BrandButton variant="ghost" onClick={() => onOpenChange(false)}>
              {t("loginModal.cancel")}
            </BrandButton>
          </DialogFooter>
        );

      case "preview":
        return (
          <DialogFooter className="gap-2">
            <BrandButton
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              {t("loginModal.cancel")}
            </BrandButton>
            <BrandButton
              variant="primary"
              onClick={onConfirm}
              loading={isLoading}
              disabled={isLoading}
            >
              {t("loginModal.signMessage")}
            </BrandButton>
          </DialogFooter>
        );

      case "signing":
        return (
          <DialogFooter>
            <p className="text-xs text-text-muted text-center w-full">
              {t("loginModal.checkWalletForSignature")}
            </p>
          </DialogFooter>
        );

      case "success":
        return null;

      case "error":
        return (
          <DialogFooter className="gap-2">
            <BrandButton variant="ghost" onClick={() => onOpenChange(false)}>
              {t("loginModal.close")}
            </BrandButton>
            <BrandButton variant="primary" onClick={onRetry}>
              {t("loginModal.tryAgain")}
            </BrandButton>
          </DialogFooter>
        );

      default:
        return null;
    }
  };

  const getTitle = () => {
    switch (step) {
      case "loading":
        return t("loginModal.signingIn", "Signing In");
      case "preview":
        return t("loginModal.reviewSignatureRequest");
      case "signing":
        return t("loginModal.signingMessage");
      case "success":
        return t("loginModal.success");
      case "error":
        return t("loginModal.error");
      default:
        return t("loginModal.signIn", "Sign In");
    }
  };

  const getDescription = () => {
    switch (step) {
      case "loading":
        return t("loginModal.preparingAuth", "Preparing authentication...");
      case "preview":
        return t("loginModal.reviewMessageBeforeSigning");
      case "signing":
        return t("loginModal.completeSignatureRequest");
      case "success":
        return t("loginModal.youAreSignedIn");
      case "error":
        return t("loginModal.somethingWentWrong");
      default:
        return "";
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "sm:max-w-[500px]",
          "bg-white border border-border-card shadow-xl",
        )}
      >
        {/* Brand stripe at top — mirrors BrowserWalletModal */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-brand-primary to-brand-primary-light rounded-t-lg" />

        <DialogHeader>
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-brand-primary" />
            <DialogTitle className="text-xl font-bold text-text-primary">
              {getTitle()}
            </DialogTitle>
          </div>
          <DialogDescription className="text-text-muted text-sm">
            {getDescription()}
          </DialogDescription>
        </DialogHeader>

        <div className="border-t border-border-card" />

        {renderContent()}
        {renderFooter()}
      </DialogContent>
    </Dialog>
  );
}
